import React, { useState, useEffect } from 'react';
import { db } from '../firebase'; 
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { startOfWeek, addDays, format, differenceInDays } from 'date-fns';

const PersonalPathway = ({ user }) => {
    const [logs, setLogs] = useState([]);
    const currentPhase = GET_MARKETING_PHASE();
    const cycleStart = new Date("2026-01-12"); //
    const dayOfCycle = differenceInDays(new Date(), cycleStart) + 1;

    useEffect(() => {
        if (!user) return;
        const q = query(collection(db, 'personal_chores'), where('userId', '==', user.uid));
        return onSnapshot(q, (snapshot) => {
            setLogs(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
    }, [user]);

    const logItem = async (type, label) => {
        await addDoc(collection(db, 'personal_chores'), {
            userId: user.uid,
            type,
            label,
            date: format(new Date(), 'yyyy-MM-dd'),
            timestamp: serverTimestamp()
        });
    };

    return (
        <div className="max-w-md mx-auto bg-gray-100 min-h-screen pb-20">
            {/* TOP STATS: Cycle & Marketing Phase */}
            <div className="bg-indigo-900 text-white p-6 rounded-b-3xl shadow-xl">
                <div className="flex justify-between items-start">
                    <div>
                        <p className="text-xs uppercase opacity-60 font-bold">Current Phase</p>
                        <h1 className="text-xl font-extrabold text-yellow-400">{currentPhase.name}</h1>
                    </div>
                    <div className="text-right">
                        <p className="text-xs uppercase opacity-60 font-bold">Cycle Day</p>
                        <p className="text-2xl font-black">{dayOfCycle}/56</p>
                    </div>
                </div>
            </div>

            <div className="p-4 space-y-6">
                {/* SECTION 1: SPLIT WORKOUTS (2x Week Target) */}
                <section>
                    <h3 className="text-gray-500 text-[10px] font-bold uppercase mb-2 ml-1">Physical Splits</h3>
                    <div className="grid grid-cols-2 gap-2">
                        {['Upper (SRE/ISO)', 'Abs & Obliques', 'Legs & Stability', 'Face Massage'].map(w => (
                            <button key={w} onClick={() => logItem('workout', w)}
                                className="bg-white p-3 rounded-xl shadow-sm border border-gray-200 text-left text-xs font-bold hover:bg-green-50">
                                💪 {w}
                            </button>
                        ))}
                    </div>
                </section>

                {/* SECTION 2: MARKETING ASSETS (Weekly Focus) */}
                <section className="bg-white rounded-2xl p-4 shadow-sm">
                    <h3 className="text-indigo-800 text-xs font-bold mb-3">🚀 {currentPhase.name} Assets</h3>
                    <div className="flex flex-wrap gap-2">
                        {currentPhase.assets.map(asset => (
                            <button key={asset} onClick={() => logItem('marketing', `${currentPhase.name}: ${asset}`)}
                                className="bg-indigo-50 text-indigo-700 px-3 py-2 rounded-full text-[10px] font-bold border border-indigo-100">
                                {asset}
                            </button>
                        ))}
                    </div>
                </section>
            </div>
            
            {/* BOTTOM LOG FEED */}
            <div className="px-6">
                <p className="text-[10px] text-gray-400 font-bold uppercase mb-2">Today's Progress</p>
                {logs.filter(l => l.date === format(new Date(), 'yyyy-MM-dd')).map(l => (
                    <div key={l.id} className="bg-white mb-1 p-2 rounded text-[11px] border-l-4 border-indigo-500 shadow-sm">
                        {l.label}
                    </div>
                ))}
            </div>
        </div>
    );
};

const CATEGORIES = [
  { id: 'upper', label: 'Upper Body (ISO/SRE)', color: 'bg-blue-50', icon: '💪' },
  { id: 'abs', label: 'Abs & Obliques', color: 'bg-green-50', icon: '🧘' },
  { id: 'legs', label: 'Legs & Stability', color: 'bg-orange-50', icon: '🦵' },
  { id: 'cycle', label: 'Cycle/PCT Tracking', color: 'bg-red-50', icon: '🧪' },
  { id: 'marketing', label: 'Marketing & Links', color: 'bg-purple-50', icon: '🔗' },
  { id: 'dev', label: 'Code & Video Prep', color: 'bg-indigo-50', icon: '💻' }
];
const GET_MARKETING_PHASE = () => {
    const today = new Date();
    const phases = [
        { name: "Mining Engineering", start: "2026-02-02", end: "2026-02-15", assets: ["Mining Demo", "Argot", "Exam", "Scenario", "Unit 1", "Pathway"] },
        { name: "Medical Professional", start: "2026-02-16", end: "2026-03-01", assets: ["Medical Demo", "Argot", "Exam", "Scenario", "Unit 1", "Pathway"] },
        { name: "Oil & Gas", start: "2026-03-02", end: "2026-03-15", assets: ["Oil&Gas Demo", "Argot", "Exam", "Scenario", "Unit 1", "Pathway"] },
        { name: "Aviation", start: "2026-03-16", end: "2026-03-29", assets: ["Aviation Demo", "Argot", "Exam", "Scenario", "Unit 1", "Pathway"] },
        { name: "Aestheticians", start: "2026-03-30", end: "2026-04-12", assets: ["Aesthetician Demo", "Argot", "Exam", "Scenario", "Unit 1", "Pathway"] },
        { name: "Legal English", start: "2026-04-13", end: "2026-04-26", assets: ["Legal Demo", "Argot", "Exam", "Scenario", "Unit 1", "Pathway"] },
        { name: "Hotel & Hospitality", start: "2026-04-27", end: "2026-05-10", assets: ["Hotel Demo", "Argot", "Exam", "Scenario", "Unit 1", "Pathway"] },
        { name: "Cabin Crew", start: "2026-05-11", end: "2026-05-24", assets: ["Cabin Demo", "Argot", "Exam", "Scenario", "Unit 1", "Pathway"] },
        { name: "Young Genius", start: "2026-05-25", end: "2026-05-31", assets: ["YG Quiz", "Dream Achiever Form", "Unit 1"] }
    ];

    return phases.find(p => today >= new Date(p.start) && today <= new Date(p.end)) || { name: "Planning", assets: [] };
};

const PersonalChoreScheduler = ({ user }) => {
    const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
    const [chores, setChores] = useState([]);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

    // Cycle Constants (e.g., Pregnenolone solo Jan 12 - March 12)
    const cycleStart = new Date(2026, 0, 12); 

    useEffect(() => {
        const q = query(collection(db, 'personal_chores'), where('userId', '==', user.uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setChores(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsubscribe();
    }, [user.uid]);

    const handleAddChore = async (dateStr, categoryId) => {
        const label = prompt(`Enter ${categoryId} detail (e.g., UP-SRE or Link Name):`);
        if (!label) return;

        await addDoc(collection(db, 'personal_chores'), {
            userId: user.uid,
            date: dateStr,
            categoryId,
            label,
            completed: false,
            createdAt: new Date()
        });
    };

    return (
        <div className="max-w-md mx-auto bg-white min-h-screen pb-20 font-sans">
            {/* Mobile Header */}
            <div className="sticky top-0 bg-indigo-900 text-white p-4 shadow-lg z-20">
                <div className="flex justify-between items-center mb-2">
                    <h1 className="text-lg font-bold">Personal Pathway</h1>
                    <span className="text-xs bg-red-500 px-2 py-1 rounded">
                        Cycle Day: {differenceInDays(new Date(), cycleStart) + 1}
                    </span>
                </div>
                <div className="flex justify-between text-sm">
                    <button onClick={() => setCurrentWeekStart(addDays(currentWeekStart, -7))}>◀</button>
                    <span>{format(currentWeekStart, 'MMM dd')} - {format(addDays(currentWeekStart, 6), 'MMM dd')}</span>
                    <button onClick={() => setCurrentWeekStart(addDays(currentWeekStart, 7))}>▶</button>
                </div>
            </div>

            {/* Category Rows */}
            <div className="flex flex-col">
                {CATEGORIES.map(cat => (
                    <div key={cat.id} className="border-b">
                        <div className={`p-2 text-xs font-bold uppercase tracking-wider ${cat.color} text-gray-600`}>
                            {cat.icon} {cat.label}
                        </div>
                        <div className="flex overflow-x-auto p-2 gap-2">
                            {weekDays.map(day => {
                                const dateStr = format(day, 'yyyy-MM-dd');
                                const dayChores = chores.filter(c => c.date === dateStr && c.categoryId === cat.id);
                                
                                return (
                                    <div 
                                        key={dateStr} 
                                        onClick={() => handleAddChore(dateStr, cat.id)}
                                        className={`min-w-[80px] h-16 rounded border-2 flex flex-col items-center justify-center relative
                                            ${format(day, 'EEE') === format(new Date(), 'EEE') ? 'border-indigo-400 bg-indigo-50' : 'border-gray-100 bg-gray-50'}`}
                                    >
                                        <span className="text-[10px] text-gray-400 absolute top-1">{format(day, 'EEE')}</span>
                                        {dayChores.map(dc => (
                                            <div key={dc.id} className="text-[9px] bg-white border px-1 rounded shadow-sm text-center truncate w-full">
                                                {dc.label}
                                            </div>
                                        ))}
                                        {dayChores.length === 0 && <span className="text-gray-300 text-lg">+</span>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PersonalChoreScheduler;